/**
 * TrackingRedirect - Handles shortcode-based tracking links
 * 
 * URL: /t/:shortcode
 * 
 * This component:
 * 1. Extracts the shortcode from the URL
 * 2. Calls the API to resolve and track the click
 * 3. Redirects to the destination URL
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

export default function TrackingRedirect() {
  const { shortcode } = useParams<{ shortcode: string }>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shortcode) {
      setError('Invalid link');
      return;
    }

    const resolveAndRedirect = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/tracking/resolve/${shortcode}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('This link has expired or is invalid.');
          } else {
            setError('Something went wrong. Please try again.');
          }
          return;
        }

        const data = await response.json();
        
        if (data.url) {
          // Redirect to the destination URL
          window.location.href = data.url;
        } else {
          setError('Invalid link destination.');
        }
      } catch (err) {
        console.error('Error resolving tracking link:', err);
        setError('Unable to process this link. Please try again later.');
      }
    };

    resolveAndRedirect();
  }, [shortcode]);

  // Show loading or error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8">
        {error ? (
          <div className="space-y-4">
            <div className="text-6xl">🔗</div>
            <h1 className="text-2xl font-semibold text-gray-800">Link Error</h1>
            <p className="text-gray-600">{error}</p>
            <a 
              href="https://helppet.ai" 
              className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to HelpPet.ai
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
            <p className="text-gray-600">Redirecting...</p>
          </div>
        )}
      </div>
    </div>
  );
}
